import clsx from 'clsx';
import { AlertTriangle, Check, Info, Loader2, X, XCircle } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { formatShortTime } from '../lib/format';
import { useI18n } from '../lib/i18n';

/* -------------------------------------------------------------------------- */
/* Buttons                                                                     */
/* -------------------------------------------------------------------------- */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'signal';
type ButtonSize = 'sm' | 'md' | 'lg';

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-600 active:bg-primary-700 shadow-sm',
  secondary:
    'bg-[var(--surface-raised)] text-[var(--text-primary)] border hairline hover:border-primary/60 hover:text-primary dark:hover:text-primary-400',
  ghost: 'text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]',
  // `error.fill` rather than `error.DEFAULT` — the darker fill clears 4.5:1 with a white label.
  danger: 'bg-error-fill text-white hover:brightness-110 shadow-sm',
  signal: 'bg-signal text-ink-deep hover:bg-signal-500 shadow-sm font-semibold',
};

const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-body-sm gap-1.5 rounded-sm',
  md: 'h-10 px-4 text-button gap-2 rounded-md',
  lg: 'h-12 px-6 text-button gap-2 rounded-md',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  icon?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  icon,
  className,
  children,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center font-medium whitespace-nowrap',
        'transition-colors duration-fast ease-precision',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        BUTTON_SIZES[size],
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : icon}
      {children}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Surfaces                                                                    */
/* -------------------------------------------------------------------------- */

export function Card({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div className={clsx('surface rounded-lg', padded && 'p-5', className)}>{children}</div>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
  className,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={clsx('flex items-start justify-between gap-4 mb-4', className)}>
      <div className="min-w-0">
        <h3 className="text-h3 text-[var(--text-primary)] truncate">{title}</h3>
        {subtitle && <p className="text-body-sm text-muted mt-0.5">{subtitle}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

/**
 * The action keeps to the end of the title's row (right; left in Arabic) on
 * every page. The title block grows from a fixed basis, not from its text's
 * width, so a long subtitle wraps under the title instead of pushing the action
 * onto a row of its own; only a phone-width screen moves it below.
 */
export function PageHeader({
  title,
  subtitle,
  count,
  action,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  /**
   * How many rows the list holds. Rendered as a pill beside the title — a bare
   * number in the subtitle slot reads as an orphan, and a counted phrase would
   * need Arabic plural agreement that a pill sidesteps in both languages.
   */
  count?: number;
  action?: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div className="min-w-0 grow basis-64">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <h1 className="text-h1 text-[var(--text-primary)]">{title}</h1>
          {count !== undefined && (
            <Badge tone="neutral" className="tabular" title={`${t('common.total')}: ${count}`}>
              <span className="sr-only">{t('common.total')}: </span>
              {count}
            </Badge>
          )}
        </div>
        {subtitle && <p className="text-body text-muted mt-1">{subtitle}</p>}
      </div>
      {action && <div className="flex max-w-full flex-wrap items-center gap-2">{action}</div>}
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/* Badges & status                                                             */
/* -------------------------------------------------------------------------- */

export type BadgeTone = 'neutral' | 'brand' | 'success' | 'warning' | 'error' | 'info' | 'plasma';

/**
 * Every tone reads at 4.5:1 or better in both themes, whatever it sits on. The
 * light theme takes the tokens' dark text shades, and the four statuses their
 * own pale fill too: green over a tint falls short on a hovered row. The dark
 * theme keeps the console's bright text over a tint of the hue.
 */
const BADGE_TONES: Record<BadgeTone, string> = {
  neutral: 'bg-neutral-500/12 text-[var(--text-secondary)] ring-neutral-500/25',
  brand: 'bg-primary/12 text-primary-600 ring-primary/30 dark:text-primary-400',
  success: 'bg-success-bg text-success-fg ring-success/30 dark:bg-success/12 dark:text-success',
  warning: 'bg-warning-bg text-warning-fg ring-warning/30 dark:bg-warning/14 dark:text-warning-dark',
  error: 'bg-error-bg text-error-fg ring-error/30 dark:bg-error/12 dark:text-error-dark',
  info: 'bg-info-bg text-info-fg ring-info/30 dark:bg-info/12 dark:text-info-dark',
  plasma: 'bg-ai-plasma/12 text-ai-plasma-text ring-ai-plasma/30 dark:text-ai-plasma-lift',
};

export function Badge({
  children,
  tone = 'neutral',
  className,
  dot = false,
  title,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
  dot?: boolean;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={clsx(
        'inline-flex items-center gap-1.5 rounded-pill px-2.5 py-0.5',
        'text-caption font-medium ring-1 ring-inset whitespace-nowrap',
        BADGE_TONES[tone],
        className,
      )}
    >
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}

/** One place decides what colour a status is, so the whole console agrees. */
export function statusTone(status: string | null | undefined): BadgeTone {
  switch (status) {
    case 'DONE':
    case 'RESOLVED':
    case 'CLOSED':
    case 'PAID':
    case 'APPROVED':
    case 'COMPLETED':
    case 'ACTIVE':
      return 'success';
    case 'IN_PROGRESS':
    case 'IN_REVIEW':
    case 'SUPPORT':
    case 'ASSIGNED':
    case 'AVAILABLE':
    case 'CALCULATED':
    case 'ISSUED':
    case 'PARTIALLY_PAID':
      return 'info';
    case 'BLOCKED':
    case 'WAITING_REQUESTER':
    case 'ON_HOLD':
    case 'PENDING':
    case 'SUBMITTED':
      return 'warning';
    case 'CANCELLED':
    case 'OVERDUE':
    case 'REJECTED':
    case 'REOPENED':
      return 'error';
    case 'NEW':
    case 'URGENT':
      return 'brand';
    case 'LOCKED':
    case 'APPLIED_TO_PAYROLL':
    case 'TESTING':
    case 'STARTED':
      return 'plasma';
    default:
      return 'neutral';
  }
}

export function priorityTone(priority: string | null | undefined): BadgeTone {
  switch (priority) {
    case 'URGENT':
      return 'error';
    case 'HIGH':
      return 'warning';
    case 'MEDIUM':
      return 'info';
    default:
      return 'neutral';
  }
}

export function StatusDot({ tone = 'neutral', pulse = false }: { tone?: 'online' | 'idle' | 'offline' | 'alert' | 'neutral'; pulse?: boolean }) {
  const colors: Record<string, string> = {
    online: 'bg-status-online',
    idle: 'bg-status-idle',
    offline: 'bg-status-offline',
    alert: 'bg-status-alert',
    neutral: 'bg-neutral-500',
  };
  return (
    <span className="relative inline-flex h-2 w-2 shrink-0" aria-hidden>
      {pulse && <span className={clsx('absolute inset-0 rounded-full animate-pulse-signal', colors[tone])} />}
      <span className={clsx('relative h-2 w-2 rounded-full', colors[tone])} />
    </span>
  );
}

/**
 * "08:00–16:00" — or, in Arabic, "8:00 م–4:00 ص" read from the right; "…" for an end
 * not reached yet. Each time is isolated and the range keeps the page's direction:
 * forced left to right, an Arabic AM/PM marker was pulled across the dash and the
 * range read scrambled (2026-10-27).
 */
export function TimeRange({
  from,
  to,
  className,
}: {
  from: string | number | Date;
  to: string | number | Date | null;
  className?: string;
}) {
  const { locale } = useI18n();
  return (
    <span className={clsx('whitespace-nowrap', className)}>
      <bdi>{formatShortTime(new Date(from), locale)}</bdi>–{to == null ? '…' : <bdi>{formatShortTime(new Date(to), locale)}</bdi>}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Form controls                                                               */
/* -------------------------------------------------------------------------- */

const FIELD_BASE =
  'w-full rounded-md border hairline bg-[var(--surface-raised)] px-3 text-body text-[var(--text-primary)] ' +
  'placeholder:text-[var(--text-tertiary)] dark:placeholder:text-[var(--text-secondary)] transition-colors duration-fast ' +
  'focus:border-primary disabled:opacity-60 disabled:cursor-not-allowed';

export function Field({
  label,
  hint,
  error,
  required,
  children,
  className,
}: {
  label?: ReactNode;
  hint?: ReactNode;
  error?: string | null;
  required?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={clsx('block', className)}>
      {label && (
        <span className="mb-1.5 flex items-center gap-1 text-body-sm font-medium text-[var(--text-secondary)]">
          {label}
          {required && <span className="text-primary dark:text-primary-400">*</span>}
        </span>
      )}
      {children}
      {error ? (
        <span className="mt-1 block text-caption text-error-fg dark:text-error-dark">{error}</span>
      ) : (
        hint && <span className="mt-1 block text-caption text-faint">{hint}</span>
      )}
    </label>
  );
}

export function Input({ className, ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={clsx(FIELD_BASE, 'h-10', className)} {...props} />;
}

export function Textarea({ className, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={clsx(FIELD_BASE, 'py-2 min-h-[88px] resize-y', className)} {...props} />;
}

export function Select({ className, children, ...props }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={clsx(FIELD_BASE, 'h-10 pe-8 appearance-none cursor-pointer', className)} {...props}>
      {children}
    </select>
  );
}

export function Checkbox({
  label,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode }) {
  return (
    <label className={clsx('inline-flex items-center gap-2 cursor-pointer select-none', className)}>
      <input
        type="checkbox"
        className="h-4 w-4 rounded-xs border-2 hairline bg-transparent accent-primary cursor-pointer"
        {...props}
      />
      {label && <span className="text-body-sm text-[var(--text-secondary)]">{label}</span>}
    </label>
  );
}

/* -------------------------------------------------------------------------- */
/* Modal                                                                       */
/* -------------------------------------------------------------------------- */

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Rendered at the end of `<body>`, not where it is used: opened from the top
 * bar, it would otherwise share the header's layer, under the form pages'
 * sticky Save bar. Focus moves in — the dialog takes it unless a field inside
 * did (`autoFocus`) — stays in while it is open, and goes back where it was
 * when it closes.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  size = 'md',
  role = 'dialog',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** `alertdialog` for a question that has to be answered before going on. */
  role?: 'dialog' | 'alertdialog';
}) {
  const { t } = useI18n();
  const titleId = useId();
  const bodyId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  // The latest onClose without re-running the effect: a parent that re-renders
  // every second (the shift clock) would otherwise pull the focus back each time.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // Where the focus was when it opened, to give it back. Read while rendering:
  // a field inside with autoFocus takes the focus before any effect runs.
  const returnFocus = useRef<HTMLElement | null>(null);
  // What inside has the focus first: that field, else the dialog itself.
  const firstFocus = useRef<HTMLElement | null>(null);
  const wasOpen = useRef(false);
  if (open !== wasOpen.current) {
    wasOpen.current = open;
    if (open) {
      returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      firstFocus.current = null;
    }
  }

  useEffect(() => {
    if (!open) return;
    const dialog = dialogRef.current;
    const active = document.activeElement;
    if (dialog && active instanceof HTMLElement && dialog.contains(active)) firstFocus.current = active;
    // Outside: nothing inside took it, or StrictMode is running this again after
    // the cleanup below gave the focus back.
    else (firstFocus.current?.isConnected ? firstFocus.current : dialog)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeRef.current();
      if (e.key !== 'Tab' || !dialog) return;
      // aria-modal says the page behind is out of reach, so Tab goes round inside.
      const focusable = dialog.querySelectorAll<HTMLElement>(FOCUSABLE);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) {
        e.preventDefault();
      } else if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
      returnFocus.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const widths = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' };

  return createPortal(
    <div className="fixed inset-0 z-modal flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div
        className="fixed inset-0 bg-ink-deep/70 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div
        ref={dialogRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={role === 'alertdialog' ? bodyId : undefined}
        tabIndex={-1}
        className={clsx(
          'relative w-full surface rounded-xl shadow-xl animate-boot-in my-auto outline-none',
          widths[size],
        )}
      >
        <div className="flex items-center justify-between gap-4 border-b hairline px-5 py-4">
          <h2 id={titleId} className="text-h3 text-[var(--text-primary)]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-sm p-1 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div id={bodyId} className="px-5 py-5">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t hairline px-5 py-4">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/**
 * A yes or no before an action. Nothing is typed, so a dialog rather than a
 * page — the no-popups rule is for forms. The yes is named after the action and
 * takes the focus, unless it is the dangerous one; Cancel, Escape, the backdrop
 * and the close button are the no. Nothing closes it while the action is under
 * way: whoever opened it closes it when the action settles.
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  danger = false,
  busy = false,
  onConfirm,
  onCancel,
}: {
  title: ReactNode;
  children?: ReactNode;
  confirmLabel: ReactNode;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const cancel = () => {
    if (!busy) onCancel();
  };
  return (
    <Modal
      open
      onClose={cancel}
      title={title}
      size="sm"
      role="alertdialog"
      footer={
        <>
          <Button onClick={cancel} disabled={busy} autoFocus={danger}>
            {t('common.cancel')}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} loading={busy} onClick={onConfirm} autoFocus={!danger}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-2 text-body text-[var(--text-secondary)]">{children}</div>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */
/* Toasts                                                                      */
/* -------------------------------------------------------------------------- */

type ToastTone = 'success' | 'error' | 'info';
interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
}

interface ToastContextValue {
  toast: (message: string, tone?: ToastTone) => void;
  success: (message: string) => void;
  error: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const toast = useCallback((message: string, tone: ToastTone = 'info') => {
    const id = Date.now() + Math.random();
    setToasts((current) => [...current, { id, tone, message }]);
    setTimeout(() => setToasts((current) => current.filter((x) => x.id !== id)), 5000);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      toast,
      success: (m: string) => toast(m, 'success'),
      error: (m: string) => toast(m, 'error'),
    }),
    [toast],
  );

  const icons: Record<ToastTone, ReactNode> = {
    success: <Check className="h-4 w-4 text-success-fg dark:text-success" />,
    error: <XCircle className="h-4 w-4 text-error-fg dark:text-error-dark" />,
    info: <Info className="h-4 w-4 text-info-fg dark:text-info" />,
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-4 end-4 z-toast flex flex-col gap-2 pointer-events-none" aria-live="polite">
        {toasts.map((item) => (
          <div
            key={item.id}
            className="pointer-events-auto flex items-start gap-3 rounded-md surface-raised px-4 py-3 shadow-lg animate-boot-in max-w-sm"
          >
            <span className="mt-0.5 shrink-0">{icons[item.tone]}</span>
            <span className="text-body-sm text-[var(--text-primary)]">{item.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>.');
  return ctx;
}

/* -------------------------------------------------------------------------- */
/* States                                                                      */
/* -------------------------------------------------------------------------- */

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={clsx('h-5 w-5 animate-spin text-primary', className)} aria-hidden />;
}

export function LoadingBlock({ label }: { label?: string }) {
  const { t } = useI18n();
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-muted">
      <Spinner />
      <span className="text-body-sm">{label ?? t('common.loading')}</span>
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  icon,
  action,
}: {
  title?: string;
  hint?: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
      {icon && <div className="mb-1 text-[var(--text-tertiary)]">{icon}</div>}
      <p className="text-title text-[var(--text-primary)]">{title ?? t('common.noResults')}</p>
      <p className="text-body-sm text-muted max-w-sm">{hint ?? t('common.noResultsHint')}</p>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useI18n();
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-14 text-center">
      <AlertTriangle className="h-7 w-7 text-error-fg dark:text-error-dark" aria-hidden />
      <p className="text-title text-[var(--text-primary)]">{t('common.error')}</p>
      <p className="text-body-sm text-muted max-w-md">{message}</p>
      {onRetry && (
        <Button size="sm" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Table                                                                       */
/* -------------------------------------------------------------------------- */

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className="overflow-x-auto">
      <table className={clsx('w-full border-collapse text-body-sm', className)}>{children}</table>
    </div>
  );
}

export function Th({
  children,
  className,
  align = 'start',
  dir,
}: {
  children?: ReactNode;
  className?: string;
  align?: 'start' | 'end' | 'center';
  dir?: 'ltr' | 'rtl';
}) {
  return (
    <th
      dir={dir}
      className={clsx(
        'border-b hairline px-3 py-2.5 text-overline uppercase text-[var(--text-tertiary)] font-bold',
        align === 'start' && 'text-start',
        align === 'end' && 'text-end',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
  align = 'start',
  dir,
  colSpan,
}: {
  children?: ReactNode;
  className?: string;
  align?: 'start' | 'end' | 'center';
  dir?: 'ltr' | 'rtl';
  colSpan?: number;
}) {
  return (
    <td
      dir={dir}
      colSpan={colSpan}
      className={clsx(
        'border-b hairline px-3 py-2.5 text-[var(--text-primary)] align-middle',
        align === 'start' && 'text-start',
        align === 'end' && 'text-end',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </td>
  );
}

export function Tr({
  children,
  onClick,
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <tr
      onClick={onClick}
      className={clsx(
        'transition-colors duration-fast',
        onClick && 'cursor-pointer hover:bg-[var(--surface-sunken)]',
        className,
      )}
    >
      {children}
    </tr>
  );
}

/**
 * A real link inside a clickable row, so it opens in a new tab — ctrl-click, middle-click, the
 * right-click menu (operator, 2026-10-22: "let the ticket number link so i can open it in new tab").
 * A plain click goes where the row would; the row's own click is not fired a second time.
 */
export function RowLink({ to, children, className }: { to: string; children: ReactNode; className?: string }) {
  return (
    <Link to={to} onClick={(e) => e.stopPropagation()} className={clsx('hover:underline', className)}>
      {children}
    </Link>
  );
}

export function Pagination({
  page,
  pageCount,
  total,
  onChange,
}: {
  page: number;
  pageCount: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const { t } = useI18n();
  if (pageCount <= 1) return null;
  return (
    <div className="flex items-center justify-between gap-3 border-t hairline px-3 py-3">
      <span className="text-caption text-muted tabular">
        {t('common.page')} {page} {t('common.of')} {pageCount} · {t('common.total')}: {total}
      </span>
      <div className="flex gap-2">
        <Button size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          {t('common.previous')}
        </Button>
        <Button size="sm" disabled={page >= pageCount} onClick={() => onChange(page + 1)}>
          {t('common.next')}
        </Button>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Metrics                                                                     */
/* -------------------------------------------------------------------------- */

export function Metric({
  label,
  value,
  hint,
  tone = 'neutral',
  icon,
  labelLines = 1,
  onClick,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  tone?: 'neutral' | 'brand' | 'success' | 'warning' | 'error';
  icon?: ReactNode;
  /**
   * 2 keeps room for a label that wraps, so the values of a row of narrow tiles
   * stay level — from `sm` up, where the tiles sit side by side.
   */
  labelLines?: 1 | 2;
  onClick?: () => void;
}) {
  const accents: Record<string, string> = {
    neutral: 'text-[var(--text-primary)]',
    brand: 'text-primary-600 dark:text-primary-400',
    success: 'text-success-fg dark:text-success',
    warning: 'text-warning-fg dark:text-warning-dark',
    error: 'text-error-fg dark:text-error-dark',
  };

  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      onClick={onClick}
      className={clsx(
        // A column, so a button starts at the top like a div instead of centring
        // what it holds: in a row of tiles, every value sits at the same height.
        'surface rounded-lg p-4 text-start w-full flex flex-col',
        onClick && 'cursor-pointer transition-colors duration-fast hover:border-primary/50',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span
          className={clsx(
            'text-caption uppercase tracking-wide text-[var(--text-tertiary)]',
            labelLines === 2 && 'sm:min-h-[2lh]',
          )}
        >
          {label}
        </span>
        {icon && <span className="text-[var(--text-tertiary)] shrink-0">{icon}</span>}
      </div>
      <div className={clsx('mt-2 text-metric tabular', accents[tone])}>{value}</div>
      {hint && <div className="mt-1 text-caption text-muted">{hint}</div>}
    </Wrapper>
  );
}

export function ProgressBar({
  percent,
  tone = 'brand',
}: {
  percent: number;
  tone?: 'brand' | 'success' | 'warning' | 'error';
}) {
  const colors = {
    brand: 'bg-primary',
    success: 'bg-success',
    warning: 'bg-warning',
    error: 'bg-error-fill',
  };
  return (
    <div className="h-2 w-full overflow-hidden rounded-pill bg-[var(--surface-sunken)]">
      <div
        className={clsx('h-full rounded-pill transition-[width] duration-base ease-precision', colors[tone])}
        style={{ width: `${Math.min(100, Math.max(0, percent))}%` }}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Tabs                                                                        */
/* -------------------------------------------------------------------------- */

export function Tabs({
  tabs,
  active,
  onChange,
}: {
  tabs: Array<{ id: string; label: ReactNode; count?: number }>;
  active: string;
  onChange: (id: string) => void;
}) {
  return (
    // Scrolls sideways when the tabs outgrow a narrow screen, never up and down: with
    // overflow-x set, the browser makes overflow-y scroll too, and Windows then drew a
    // stray vertical scrollbar at the end of the row.
    <div className="flex gap-1 overflow-x-auto overflow-y-hidden border-b hairline" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={active === tab.id}
          onClick={() => onChange(tab.id)}
          className={clsx(
            'relative whitespace-nowrap px-4 py-2.5 text-body-sm font-medium transition-colors duration-fast',
            active === tab.id
              ? 'text-primary-600 dark:text-primary-400'
              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
          )}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span className="ms-1.5 text-caption text-[var(--text-tertiary)] tabular">{tab.count}</span>
          )}
          {active === tab.id && (
            // Inside the tab: hanging 1px below it was what overflowed the row.
            <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-pill bg-primary" aria-hidden />
          )}
        </button>
      ))}
    </div>
  );
}

/** A key/value row for detail panels. */
export function Detail({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="text-body-sm text-[var(--text-tertiary)] shrink-0">{label}</dt>
      <dd className="text-body-sm text-[var(--text-primary)] text-end min-w-0 break-words">{children}</dd>
    </div>
  );
}
